import { buildReviewThemesNamespace } from "./reviewThemes.ts";

export const en = {
    Navigation: {
        analytics: {
            label: "Analytics",
            description: "Engagement, reports, and trends",
            engagement: {
                label: "Engagement",
                main: {
                    label: "Engagement",
                    overview: { label: "Overview" },
                    metrics: { label: "Metrics" },
                    "question-results": { label: "Question results" },
                    heatmap: { label: "Heatmap" }
                },
                "metric-detail": { label: "Metric details" }
            },
            feedback: {
                label: "Feedback",
                main: {
                    label: "Feedback",
                    overview: { label: "Overview" }
                }
            },
            performance: {
                label: "Performance reviews",
                main: {
                    label: "Performance reviews",
                    overview: { label: "Scores" },
                    calibration: { label: "Calibration" },
                    themes: { label: "Themes" }
                }
            },
            recognition: {
                label: "Recognition",
                main: {
                    label: "Good Vibes report",
                    overview: { label: "Overview" }
                }
            },
            reports: {
                label: "Reports",
                engagement: { label: "Engagement" },
                feedback: { label: "Feedback" },
                "good-vibes": { label: "Good Vibes" }
            },
            meetings: {
                label: "Meetings",
                "one-on-ones": { label: "1:1s" }
            },
            workforce: {
                label: "Workforce",
                retention: { label: "Retention" },
                workforce: { label: "Workforce analytics" }
            },
            custom: {
                label: "Custom",
                custom: { label: "Custom reports" }
            },
            "manager-impact": {
                label: "Manager Impact",
                main: {
                    label: "Manager Impact",
                    overview: { label: "Manager Impact" }
                }
            }
        }
    },
    ManagerHealthDonut: {
        title: "Manager overview",
        subtitle: "Distribution of managers by impact level.",
        great: "Great",
        needsWork: "Needs work",
        info: "The share of your managers rated <strong>Great</strong> vs. <strong>Needs work</strong>, based on each manager's overall impact score."
    },
    ManagerImpactMetricInfo: {
        label: "About {{metric}}"
    },
    ManagerImpactHrPage: {
        title: "Manager Impact",
        betaBadge: "Beta",
        subtitle: "Identify managers who need support and measure the impact of the platform.",
        scope: {
            ariaLabel: "Scope",
            directReports: "My direct reports",
            all: "All managers"
        },
        summaryCards: {
            overallImpact: {
                title: "Overall manager impact",
                formula: "Practice (60%) x Outcome (40%)",
                status: {
                    great: "Great",
                    needsWork: "Needs work"
                },
                practice: {
                    label: "Practice",
                    description: "Behaviours: 1:1s, feedback, recognition, goals"
                },
                outcome: {
                    label: "Outcome",
                    description: "Results: engagement, retention, performance"
                }
            }
        },
        leaderboard: {
            title: "Manager leaderboard",
            subtitle: "Ranked by impact score, sortable by pillar.",
            ariaLabel: "Manager leaderboard",
            columns: {
                manager: "Manager",
                participationRate: "Part. rate",
                impact: "Impact",
                practice: "Practice",
                outcome: "Outcome"
            },
            columnInfo: {
                participationRate: "Percentage of members who answered a survey in the last 30 days for this manager.",
                impact: "A manager's overall score, blending their management practices (60%) and their team's outcomes (40%).",
                practice: "The management habits a manager controls: feedback replies, review submissions, goal updates, and recognition given. 60% of impact.",
                outcome: "The results a manager's team experiences: engagement, manager relationship, review scores, and goal attainment. 40% of impact."
            },
            pagination: {
                showing: "Showing {{start}}–{{end}} of {{total}}",
                previous: "Previous page",
                next: "Next page"
            },
            liveRegion: {
                sortedAscending: "Sorted by {{column}} ascending",
                sortedDescending: "Sorted by {{column}} descending"
            }
        }
    },
    EngagementFiltersHeading: {
        subtitle: "How your people feel across your engagement metrics.",
        heatmapScoreModeDisabledReason: "Score shows each team's value as of the selected date, not a range. Switch to Variation to compare over a time frame."
    },
    EngagementRating: {
        critical: "Critical",
        veryLow: "Very low",
        low: "Low",
        good: "Good",
        veryGood: "Very good",
        great: "Great",
        amazing: "Amazing"
    },
    EngagementOverviewPage: {
        liveRegion: {
            loading: "Loading engagement overview",
            loadingInsight: "Loading engagement insight"
        },
        sectionError: {
            insight: "We couldn't load the engagement insight right now.",
            history: "We couldn't load the engagement scores right now.",
            enps: "We couldn't load eNPS right now.",
            participation: "We couldn't load the participation rate right now."
        },
        sectionErrorReassurance: "Please try again later.",
        retry: "Try again",
        retrying: "Retrying…",
        timeFrame: {
            relativeLabel: {
                "7d": "7 days",
                "30d": "30 days",
                "3m": "3 months",
                "6m": "6 months",
                quarter: "this quarter",
                year: "this year",
                allTime: "all time"
            }
        }
    },
    EngagementOverviewCard: {
        title: "Overall engagement score",
        delta_one: "{{sign}}{{value}} pt {{period}}",
        delta_other: "{{sign}}{{value}} pts {{period}}",
        deltaZero: "No change {{period}}",
        deltaPeriod: {
            "7d": "vs prior 7 days",
            "30d": "vs prior 30 days",
            "3m": "vs prior 3 months",
            "6m": "vs prior 6 months",
            quarter: "vs prior quarter",
            year: "vs prior year",
            allTime: "vs prior period"
        },
        notEnoughResponses: "Not enough responses",
        notEnoughResponsesAnonymity: "Fewer than 3 responses were collected during this period. Results are hidden to protect anonymity.",
        scoreAriaLabel: "Engagement score",
        scoreNotAvailableAriaLabel: "Engagement score not available: not enough responses",
        outOf: "out of {{max}}",
        valueLabel: "{{score}} out of {{max}}",
        sparkline: {
            myTeam: "My team",
            directReports: "Direct reports",
            company: "Company"
        },
        companyAverage: {
            label: "Company average",
            deltaValue_one: "{{sign}}{{value}} pt",
            deltaValue_other: "{{sign}}{{value}} pts",
            notAvailable: "Not available"
        }
    },
    ParticipationGauge: {
        countLabel: "{{responded}} / {{invited}}"
    },
    ParticipationOverviewCard: {
        title: "Participation rate",
        tooltipLabel: "About participation rate",
        tooltip: "This is the percentage of members who answered a survey during the selected period.",
        delta_one: "{{sign}}{{value}} pt {{period}}",
        delta_other: "{{sign}}{{value}} pts {{period}}",
        deltaZero: "No change {{period}}",
        deltaPeriod: {
            "7d": "vs prior 7 days",
            "30d": "vs prior 30 days",
            "3m": "vs prior 3 months",
            "6m": "vs prior 6 months",
            quarter: "vs prior quarter",
            year: "vs prior year",
            allTime: "vs prior period"
        },
        rateAriaLabel: "Participation rate {{value}}, {{responded}} of {{invited}} people responded",
        noInvitesBandLabel: "No data",
        noInvitesNote: "No surveys were sent out during this period.",
        sparkline: {
            myTeam: "My team",
            directReports: "Direct reports",
            company: "Company",
            yAxisLabel: "Participation rate",
            ariaLabel: "Participation rate sparkline over {{period}}"
        },
        companyAverage: {
            label: "Company average",
            deltaValue_one: "{{sign}}{{value}} pt",
            deltaValue_other: "{{sign}}{{value}} pts",
            notAvailable: "Not available"
        }
    },
    EnpsOverviewCard: {
        title: "Employee Net Promoter Score",
        tooltipLabel: "About eNPS",
        tooltip: "Employees rate how likely they are to recommend working here on a 0–10 scale. Promoters score 9–10, passives 7–8, detractors 0–6.",
        intro: "Scores reflect the last 90 days of responses, independent of the date range selected above.",
        learnMore: "Learn more",
        offBanner: "eNPS surveys are off. Showing the last score from {{date}}.",
        offBannerDateless: "eNPS surveys are off. Showing the last available score.",
        offBannerNoScore: "eNPS surveys are off for your organization.",
        showDistribution: "Show distribution",
        hideDistribution: "Hide distribution",
        notEnoughResponsesAnonymity: "Fewer than 3 responses were collected during this period. Results are hidden to protect anonymity.",
        sparkline: {
            myTeam: "My team",
            directReports: "Direct reports",
            company: "Company"
        }
    },
    EngagementInsightCard: {
        notEnoughData: "Not enough data yet to generate an insight for this period.",
        seeMore: "See more",
        seeLess: "See less",
        sourcesLabel: "Sources",
        sourceChips: {
            pulse: "Pulse responses",
            feedback: "Feedback"
        },
        feedback: {
            accuracyPrompt: "Was this helpful?",
            accurate: "Accurate",
            inaccurate: "Inaccurate",
            thanks: "Thanks for your feedback!",
            reasonDialog: {
                title: "Tell us more",
                description: "What was inaccurate about this insight?",
                placeholder: "Share details (optional)",
                skip: "Skip",
                submit: "Submit"
            }
        }
    },
    EngagementPage: {
        title: "Engagement",
        empty: "Nothing here yet.",
        liveRegion: {
            loading: "Loading metrics"
        }
    },
    EngagementPageActions: {
        analyze: "Analyze Engagement",
        analyzePrompt: "Analyze the Engagement results and tell me what to focus on.",
        error: "Unable to analyze engagement. Please try again.",
        exportCsv: {
            metrics: "Export metrics as CSV",
            questionResults: "Export question results as CSV",
            heatmap: "Export heatmap as CSV"
        },
        exportExcel: {
            metrics: "Export metrics as Excel",
            questionResults: "Export question results as Excel",
            heatmap: "Export heatmap as Excel"
        },
        exportFailed: "We couldn't prepare your export. Please try again.",
        actions: "Engagement report options",
        sendReminder: "Send survey reminder",
        manageSurveySettings: "Manage survey settings",
        createCustomPresentation: {
            Pdf: "Create custom report",
            Pptx: "Create custom presentation"
        },
        createCustomPresentationPrompt: {
            Pdf: "Help me create a custom PDF report based on the engagement data for {{scope}}, covering {{timeFrame}}.",
            Pptx: "Help me create a custom presentation based on the engagement data for {{scope}}, covering {{timeFrame}}."
        },
        createCustomPresentationFailed: "We couldn't gather your data for this presentation. Please try again.",
        scopePhrase: {
            organizationFallback: "the company",
            "reporting-line": "my team",
            "direct-reports": "my direct reports"
        },
        timeFramePhrase: {
            "7d": "the last 7 days",
            "30d": "the last 30 days",
            "3m": "the last 3 months",
            "6m": "the last 6 months",
            quarter: "this quarter",
            year: "this year",
            allTime: "all time"
        },
        surveySettingsRedirect: {
            eyebrow: "Heads up",
            title: "One quick detour",
            body: "Survey settings aren't available in the new app yet. We'll take you to Officevibe to manage them. Nothing will be lost.",
            cancel: "Cancel",
            continue: "Continue in Officevibe"
        }
    },
    SendSurveyReminderDialog: {
        title: "Send survey reminder",
        count_one: "{{count}} person hasn't responded to the survey yet.",
        count_other: "{{count}} people haven't responded to the survey yet.",
        countLoading: "Checking who hasn't responded yet…",
        countUnavailable: "We couldn't check who hasn't responded yet.",
        messageLabel: "Custom message (optional)",
        messagePlaceholder: "Write a message…",
        cancel: "Cancel",
        send: "Send reminder",
        successToast: "Survey reminder sent",
        errorToast: "Couldn't send the survey reminder. Please try again."
    },
    EngagementMetricsPage: {
        title: "Metrics",
        description: "The science-backed engagement metrics behind the overall score.",
        liveRegion: {
            loading: "Loading metrics"
        },
        sectionError: "The list of metrics couldn't be loaded",
        sectionErrorReassurance: "Please try again later.",
        retry: "Try again",
        retrying: "Retrying…"
    },
    EngagementQuestionResultsPage: {
        liveRegion: {
            loading: "Loading questions"
        },
        standardQuestions: {
            heading: "Standard questions results",
            description: "Results from the science-backed questions in your pulse.",
            tableAriaLabel: "Standard questions",
            manageQuestions: "Manage questions",
            manageQuestionsRedirect: {
                eyebrow: "Heads up",
                title: "One quick detour",
                body: "Managing questions isn't available in the new app yet. We'll take you to Officevibe to manage them. Nothing will be lost.",
                cancel: "Cancel",
                continue: "Continue in Officevibe"
            }
        },
        customQuestions: {
            heading: "Custom question results",
            description: "Results from the questions your organization added.",
            tableAriaLabel: "Custom questions",
            manageQuestions: "Manage questions",
            manageQuestionsRedirect: {
                eyebrow: "Heads up",
                title: "One quick detour",
                body: "Managing custom questions isn't available in the new app yet. We'll take you to Officevibe to manage them. Nothing will be lost.",
                cancel: "Cancel",
                continue: "Continue in Officevibe"
            },
            addCustomQuestion: "Add custom question",
            newBadge: "New",
            addCustomQuestionRedirect: {
                eyebrow: "Heads up",
                title: "One quick detour",
                body: "Adding custom questions isn't available in the new app yet. We'll take you to Officevibe to add one. Nothing will be lost.",
                cancel: "Cancel",
                continue: "Continue in Officevibe"
            }
        },
        sectionError: "The list of questions couldn't be loaded",
        sectionErrorReassurance: "Please try again later.",
        retry: "Try again",
        retrying: "Retrying…"
    },
    EngagementMetricDetailNotFound: {
        heading: "Metric not found",
        body: "This metric doesn't exist or may have been removed.",
        backToMetrics: "Back to Metrics"
    },
    EngagementMetricDetailPage: {
        liveRegion: {
            loading: "Loading metric"
        },
        backToMetrics: "Back to Metrics",
        backToQuestionResults: "Back to Question results",
        tooltipLabel: "About {{metric}}",
        questionsTableAriaLabel: "{{metric}} questions",
        timeFrame: {
            relativeLabel: {
                "7d": "7 days",
                "30d": "30 days",
                "3m": "3 months",
                "6m": "6 months",
                quarter: "this quarter",
                year: "this year",
                allTime: "all time"
            }
        }
    },
    CustomQuestionDetailPage: {
        liveRegion: {
            loading: "Loading question"
        },
        backToQuestionResults: "Back to Question results",
        eyebrow: "Custom question",
        status: {
            active: "Active",
            paused: "Paused"
        },
        meta: {
            updated: "Updated {{date}}",
            createdBy: "Created by {{name}}"
        },
        timeFrame: {
            relativeLabel: {
                "7d": "7 days",
                "30d": "30 days",
                "3m": "3 months",
                "6m": "6 months",
                quarter: "this quarter",
                year: "this year",
                allTime: "all time"
            }
        },
        periodLabel: {
            "7d": "7 days",
            "30d": "30 days",
            "3m": "3 months",
            "6m": "6 months",
            quarter: "the quarter",
            year: "the year",
            allTime: "your entire history"
        },
        deltaPeriod: {
            "7d": "vs prior 7 days",
            "30d": "vs prior 30 days",
            "3m": "vs prior 3 months",
            "6m": "vs prior 6 months",
            quarter: "vs prior quarter",
            year: "vs prior year",
            allTime: "vs prior period"
        },
        delta_one: "{{sign}}{{value}} pt {{period}}",
        delta_other: "{{sign}}{{value}} pts {{period}}",
        deltaZero: "No change {{period}}",
        notEnoughResponses: "Not enough responses",
        scoreAriaLabel: "Question score",
        scoreNotAvailableAriaLabel: "Question score not available: not enough responses",
        outOf: "out of {{max}}",
        valueLabel: "{{score}} out of {{max}}",
        sparkline: {
            myTeam: "Your team",
            directReports: "Direct reports",
            company: "Company",
            yAxisLabel: "Score"
        },
        latestScore: {
            title: "Latest score"
        },
        companyAverage: {
            label: "Company score",
            deltaValue_one: "{{sign}}{{value}} pt",
            deltaValue_other: "{{sign}}{{value}} pts",
            deltaZero: "no change {{period}}",
            notAvailable: "Not available"
        },
        participation: {
            title: "Participation rate over {{period}}",
            rate: "{{rate}}%",
            noData: "–"
        },
        responses: {
            title: "Responses"
        }
    },
    CustomQuestionResponsesDistribution: {
        noResponses: "No responses yet.",
        percentage: "{{percentage}}%",
        people_one: "{{count}} person",
        people_other: "{{count}} people"
    },
    CustomQuestionDetailNotFound: {
        heading: "Custom question not found",
        body: "This custom question doesn't exist or may have been removed.",
        backToQuestionResults: "Back to Question results"
    },
    EngagementVariationBadge: {
        increased: "Increased",
        decreased: "Decreased",
        noComparison: "No comparison available"
    },
    EngagementMetricsTable: {
        ariaLabel: "Engagement metrics",
        noScore: "No score",
        tooltipLabel: "About {{metric}}",
        deltaValue_one: "{{value}} pt",
        deltaValue_other: "{{value}} pts",
        columns: {
            metric: "Metric",
            score: "Score",
            change: "Change",
            changeSubtitle: "over {{period}}",
            details: "Open metric details"
        },
        periodLabel: {
            "7d": "7 days",
            "30d": "30 days",
            "3m": "3 months",
            "6m": "6 months",
            quarter: "the quarter",
            year: "the year",
            allTime: "your entire history"
        },
        liveRegion: {
            sortChanged: "Sorted by {{column}} {{direction}}",
            ascending: "ascending",
            descending: "descending",
            columns: {
                name: "Metric",
                score: "Score",
                scoreDelta: "Change"
            }
        }
    },
    EngagementPeriodToolbar: {
        disabledInfoLabel: "Why is this disabled?",
        timeFrame: {
            ariaLabel: "Time frame",
            clearAriaLabel: "Clear time frame filter",
            "7d": "Last 7 days",
            "30d": "Last 30 days",
            "3m": "Last 3 months",
            "6m": "Last 6 months",
            quarter: "This quarter",
            year: "This year",
            allTime: "All time"
        }
    },
    FeedbackPeriodToolbar: {
        ariaLabel: "Time frame",
        clearAriaLabel: "Clear time frame filter",
        timeFrames: {
            "30d": "Last 30 days",
            "3m": "Last 3 months",
            "6m": "Last 6 months",
            quarter: "This quarter",
            year: "This year"
        },
        custom: "Custom period",
        rangeLabel: "Start and end date",
        invalidRange: "The end date can't be before the start date.",
        rangeTooLong: "A custom period can't be longer than 3 years."
    },
    GoodVibesTimeFrameSelect: {
        ariaLabel: "Time frame",
        clearAriaLabel: "Clear time frame filter",
        timeFrames: {
            "7d": "Last 7 days",
            "30d": "Last 30 days",
            "3m": "Last 3 months",
            "6m": "Last 6 months",
            quarter: "This quarter",
            year: "This year"
        }
    },
    GoodVibesVisibilitySelect: {
        ariaLabel: "Visibility",
        clearAriaLabel: "Clear visibility filter",
        triggerPrefix: "Visibility:",
        visibilities: {
            all: "All Good Vibes",
            public: "Public",
            direct: "Direct"
        }
    },
    FeedbackFilterChip: {
        chipLabel: "{{dimension}}: {{value}}",
        clearFilter: "Clear filter",
        removeFilter: "Remove {{dimension}} filter",
        loading: "Loading…"
    },
    FeedbackFilterBar: {
        addFilters: "Add filters",
        clearAllFilters: "Clear all filters",
        dimensions: {
            sentiment: "Sentiment",
            metricId: "Metric",
            feedbackType: "Type"
        },
        errors: {
            metricId: "We couldn't load the metrics."
        },
        unavailable: {
            metricId: "Unavailable metric"
        },
        sentiments: {
            Positive: "Positive",
            Constructive: "Constructive"
        },
        types: {
            Officevibe: "Pulse survey: Officevibe questions",
            Custom: "Pulse survey: Custom questions",
            Onboarding: "Onboarding survey",
            NonSurvey: "Non-survey",
            Smart: "Smart questions"
        }
    },
    ExportMenu: {
        label: "Export",
        preparing: "Preparing",
        pdf: "Export as PDF",
        csv: "Export as CSV",
        csvFailed: "The export couldn't be prepared. Try again."
    },
    AnalyticsPageActions: {
        actions: "Analyze, export and share",
        share: "Share",
        preparing: "Preparing"
    },
    AskWorkleapAi: {
        label: "Analyze Performance",
        liveRegion: {
            collecting: "Collecting the performance results"
        },
        noData: "The performance results couldn't be loaded, so there's nothing to analyze yet. Try again.",
        failed: "Workleap AI couldn't be reached. Try again.",
        prompt: "Analyze the Performance results and tell me what to focus on.",
        instructions:
            "The user is looking at a Workleap Performance analytics view. Its numbers are in the provided context. Ground every statement in those numbers — never invent a figure, and say plainly when the data doesn't support a conclusion. Where several series are present, the first is the view the user is on and the others are its comparisons. Lead with what stands out, then what it likely means, then what to look at next. Be concise, and don't read the raw numbers back.",
        filters: {
            directManagers_one: "reports of {{count}} direct manager",
            directManagers_other: "reports of {{count}} direct managers",
            reportingLine_one: "reporting line of {{count}} manager",
            reportingLine_other: "reporting lines of {{count}} managers",
            segment_one: "{{count}} segment",
            segment_other: "{{count}} segments",
            department_one: "{{count}} department",
            department_other: "{{count}} departments",
            gender_one: "{{count}} gender",
            gender_other: "{{count}} genders",
            workLocation_one: "{{count}} work location",
            workLocation_other: "{{count}} work locations",
            roleLevel_one: "{{count}} role level",
            roleLevel_other: "{{count}} role levels",
            jobTitle_one: "{{count}} job title",
            jobTitle_other: "{{count}} job titles",
            tenure: "tenure {{band}}",
            track: "track {{track}}"
        }
    },
    ShareViewDialog: {
        title: "Share",
        linkLabel: "Link",
        copy: "Copy",
        copied: "Copied",
        audienceHint: "People with access to this audience can open the link.",
        emailCta: "Email a link",
        done: "Done",
        email: {
            subject: "{{surface}} in Workleap",
            intro: "Here is the performance view."
        },
        surface: {
            performance: "Performance reviews",
            overview: "Scores",
            calibration: "Calibration",
            themes: "Themes"
        },
        view: {
            "nine-box": "9-box grid"
        }
    },
    PerformanceOverviewPage: {
        heading: "Review scores",
        csvUnavailable: "Available once the scores have loaded",
        seriesFilterLabel: "{{series}} · {{band}}",
        bandFilterTile: {
            below: "Score below {{max}}",
            between: "Score between {{min}} and {{max}}",
            atLeast: "Score {{min}} and above"
        },
        cellFilterTile: "{{cell}}: {{potential}} potential, {{performance}} performance",
        empty: {
            title: "No reviewees yet",
            description: "Once reviews are shared, scores for your organization will show up here."
        },
        emptyFiltered: {
            title: "No reviewees match these filters",
            description: "Try removing a filter to widen the results."
        },
        liveRegion: {
            loading: "Loading review scores",
            loadingNineBox: "Loading the 9-box grid"
        }
    },
    ScoreDistributionSection: {
        view: {
            groupLabel: "Score view",
            histogram: "Distribution",
            nineBox: "9-box grid"
        }
    },
    NineBoxGrid: {
        heading: "Performance and potential",
        axis: {
            high: "High",
            potential: "Potential",
            low: "Low",
            performance: "Performance",
            performanceBand: {
                Low: "low",
                Medium: "medium",
                High: "high"
            },
            potentialBand: {
                Low: "low",
                Medium: "medium",
                High: "high"
            }
        },
        percentage: "{{percentage}}%",
        cell: {
            ariaLabel_one: "{{label}}, {{performance}} performance and {{potential}} potential, {{count}} person, {{percentage}}%",
            ariaLabel_other: "{{label}}, {{performance}} performance and {{potential}} potential, {{count}} people, {{percentage}}%",
            seriesAriaLabel_one: "{{series}}, {{label}}, {{performance}} performance and {{potential}} potential, {{count}} person",
            seriesAriaLabel_other: "{{series}}, {{label}}, {{performance}} performance and {{potential}} potential, {{count}} people",
            seriesNoDataAriaLabel: "{{series}}, {{label}}, {{performance}} performance and {{potential}} potential, no data"
        }
    },
    RevieweesTable: {
        ariaLabel: "Reviewees",
        caption: {
            total_one: "{{count}} reviewee",
            total_other: "{{count}} reviewees"
        },
        graphFilter: {
            removeLabel: "Remove filter"
        },
        search: {
            placeholder: "Search members",
            ariaLabel: "Search reviewees"
        },
        noResults: "No people match your search.",
        noData: "No reviewees to show for this selection.",
        noScore: "No score",
        comparisonNote: "Each person belongs to a single group, so the comparison applies to the distribution above rather than to these rows.",
        unknownMember: "Unknown member",
        liveRegion: {
            loading: "Loading reviewees",
            loadingMoreAnnouncement: "Loading more reviewees",
            loadedMore_one: "Loaded {{count}} more reviewee",
            loadedMore_other: "Loaded {{count}} more reviewees",
            results_one: "{{count}} result",
            results_other: "{{count}} results",
            sortedAscending: "Sorted by {{column}} ascending",
            sortedDescending: "Sorted by {{column}} descending"
        }
    },
    NineBoxCellLabels: {
        cell: {
            enigma: "Enigma",
            growth: "Growth",
            stars: "Stars",
            dilemma: "Dilemma",
            core: "Core",
            "high-impact": "High impact",
            risk: "Risk",
            effective: "Effective",
            trusted: "Trusted"
        }
    },
    PerformanceCalibrationPage: {
        heading: "Calibration signals"
    },
    CalibrationStatsRow: {
        inScope: {
            label: "In scope",
            people_one: "Person",
            people_other: "People",
            reviews_one: "Review",
            reviews_other: "Reviews"
        },
        average: {
            label: "Avg review score",
            unit: "/ {{maxScale}}",
            ariaValue: "{{value}} out of {{maxScale}}",
            noScore: "No score yet"
        },
        distribution: {
            label: "Review score distribution",
            percentage: "{{percentage}}%"
        },
        liveRegion: {
            loading: "Loading calibration summary"
        }
    },
    ReviewerTypeAveragesStat: {
        label: "Rating avg by type"
    },
    ReviewerTypes: {
        self: "Self",
        manager: "Manager",
        peer: "Peer",
        upward: "Upward"
    },
    CalibrationDimensionSelector: {
        label: "Group by"
    },
    CalibrationDimensions: {
        cycle: "Review cycle",
        timeRange: "Time range",
        segment: "Segment",
        manager: "Direct manager",
        reportingLine: "Reporting line",
        function: "Department",
        gender: "Gender",
        workLocation: "Work location",
        roleLevel: "Role level",
        jobTitle: "Job title",
        tenure: "Tenure",
        track: "Track"
    },
    CalibrationSignalsTable: {
        title: "Calibration signals",
        ariaLabel: "Calibration signals",
        comparisonNote: "Each row already belongs to one “{{dimension}}” value, so the comparison can't be shown per row. Compare by something other than “{{dimension}}” to see it here.",
        columns: {
            expand: "Show group details"
        },
        expandGroup: "Show details for {{group}}",
        collapseGroup: "Hide details for {{group}}",
        legend: {
            moderate: "Worth a look",
            high: "Signal"
        },
        empty: "No groups have enough reviews to compare yet.",
        emptyFiltered: "No groups match these filters. Try widening them.",
        noValue: "Not available",
        unknownGroup: "Unknown group",
        liveRegion: {
            loading: "Loading calibration signals",
            loadingMoreAnnouncement: "Loading more groups",
            loadedMore_one: "Loaded {{count}} more group",
            loadedMore_other: "Loaded {{count}} more groups",
            results_one: "{{count}} group",
            results_other: "{{count}} groups",
            sortedAscending: "Sorted by {{column}} ascending",
            sortedDescending: "Sorted by {{column}} descending"
        }
    },
    CalibrationSignalInfo: {
        label: "About signals",
        title: "How a signal is set",
        intro: "Every group is read two ways: how widely its ratings spread, and how far its average sits from the company's.",
        high: "A signal — spread around {{spread}} or less, or a gap around {{gap}} or more.",
        moderate: "Worth a look — spread around {{nearSpread}} or less, or a gap around {{nearGap}} or more.",
        baseline: "Every gap is measured against the company average of {{average}}.",
        floor: "Under {{members}} rated people neither value is graded — one rating would swing it too far. The row still reports its own numbers."
    },
    CalibrationColumnInfo: {
        spread: {
            label: "About spread",
            title: "Spread",
            descriptionNoBaseline: "How widely the group's own ratings differ. A small spread means it rated almost everyone the same, so it isn't telling its performers apart.",
            thresholds: "Flagged at around {{high}} or less, and worth a look at around {{moderate}} or less."
        },
        gap: {
            label: "About gap",
            title: "Gap",
            description: "How far the group's average sits from the company average of {{average}}, above or below.",
            descriptionNoBaseline: "How far the group's average sits from the company average, above or below.",
            thresholds: "Flagged at around {{high}} or more, and worth a look at around {{moderate}} or more."
        },
        floor: "Groups under {{members}} rated people are never flagged — one rating would swing them too far."
    },
    CalibrationGroupDetail: {
        spread: {
            title: "Score distribution",
            error: "This group's score distribution couldn't be loaded.",
            retry: "Try again",
            liveRegion: {
                loading: "Loading score distribution"
            }
        },
        people: {
            title: "People"
        }
    },
    CalibrationGroupPeople: {
        empty: "No rated people in this group.",
        error: "This group's people couldn't be loaded.",
        staleError: "Couldn't refresh — showing the last results.",
        retry: "Try again",
        loadMore: "Show more people",
        noScore: "Not available",
        unknownMember: "Unknown member",
        liveRegion: {
            loading: "Loading people",
            loadedMore_one: "Loaded {{count}} more person",
            loadedMore_other: "Loaded {{count}} more people"
        }
    },
    PerformanceThemesPage: {
        strengths: {
            title: "Strengths"
        },
        watchOuts: {
            title: "Growth opportunities"
        },
        empty: "No sentiment data available.",
        emptyFiltered: "No sentiment data matches these filters. Try removing a filter to widen the results.",
        legend: {
            mentions_one: "{{mentions}} mention",
            mentions_other: "{{mentions}} mentions"
        },
        liveRegion: {
            loading: "Loading review themes"
        }
    },
    ReviewThemes: buildReviewThemesNamespace("en-US", {
        ClarityOfExpectations: "Whether people have a clear understanding of their role, priorities, and what success looks like.",
        AccountabilityAndFollowThrough: "How reliably individuals deliver on commitments and uphold ownership without needing reminders.",
        DecisionQualityAndExecutionDiscipline: "How effectively teams make timely decisions and keep work moving with focus and operational rigor.",
        GrowthLearningAndSkillDevelopment: "Whether people are actively building new skills, taking on challenges, and progressing in their capabilities.",
        FeedbackCulture: "How consistently teams give and receive timely, specific, and actionable feedback that drives improvement.",
        IndividualLeadership: "How individuals positively influence others, uplift the team, and model strong behaviors without formal authority.",
        CollaborationAndCrossFunctionalEffectiveness: "How well people work across teams to share context, align priorities, and unblock each other.",
        WorkloadAndSustainability: "Whether workload, pacing, and stress levels support healthy, sustained performance over time.",
        OutcomeOrientation: "How strongly individuals focus on meaningful impact and tie their work to customer and business results.",
        CommunicationAndTrust: "Whether people feel safe speaking openly, raising concerns early, and engaging in honest, constructive dialogue.",
        InnovationAndAdaptability: "How well individuals generate new solutions, adapt to change, and navigate ambiguity with a problem-solving mindset.",
        OwnershipAndInitiative: "Whether people take proactive responsibility, drive progress independently, and act without waiting for direction."
    }),
    PeriodFilterButton: {
        label: "Period",
        allCycles: "All cycles",
        untitled: "Untitled cycle",
        cycleCount_one: "{{count}} cycle",
        cycleCount_other: "{{count}} cycles",
        modes: "Period type",
        byCycle: "By cycle",
        byTimeRange: "By time range"
    },
    TimeRangeFilterPanel: {
        title: "Time range",
        back: "Back to dimensions",
        customRange: "Custom range",
        rangeLabel: "Start and end date",
        clear: "Clear time range",
        invalidRange: "The end date can't be before the start date."
    },
    CycleFilterList: {
        title: "Review cycles",
        back: "Back to dimensions",
        searchPlaceholder: "Search review cycle",
        allCycles: "All cycles",
        untitled: "Untitled cycle",
        activity: {
            active: "Active",
            locked: "Locked"
        },
        empty: "No review cycles found",
        noResults: "No review cycles match your search.",
        loading: "Loading review cycles",
        liveRegion: {
            loadedMore_one: "Loaded {{count}} more review cycle",
            loadedMore_other: "Loaded {{count}} more review cycles",
            results_one: "{{count}} review cycle found",
            results_other: "{{count}} review cycles found"
        },
        error: "Review cycles couldn't be loaded. Try again.",
        staleError: "Couldn't refresh — showing the last results."
    },
    ReviewCycles: {
        untitled: "Untitled cycle"
    },
    ComparisonGroups: {
        unknownPerson: "Unknown person",
        unknownSegment: "Unknown segment"
    },
    AnalyticsFiltersCard: {
        baseSeries: "Series {{letter}}: {{label}}",
        clearAll: "Clear all",
        seriesUsage_one: "{{count}} of {{total}} comparison series used",
        seriesUsage_other: "{{count}} of {{total}} comparison series used"
    },
    ComparisonRowActions: {
        addComparison: "Add comparison",
        remove: "Remove comparison: {{label}}",
        capReached: "Maximum of {{count}} comparison series reached. Remove one to add another."
    },
    BreakdownButton: {
        label: "Break down by",
        addGroup: "Add {{dimension}}",
        hint_one: "{{count}} of {{total}} comparison series used.",
        hint_other: "{{count}} of {{total}} comparison series used.",
        capReached: "Maximum of {{count}} comparison series reached. Remove one to add another.",
        done: "Done",
        liveRegion: {
            used_one: "{{count}} of {{total}} comparison series used",
            used_other: "{{count}} of {{total}} comparison series used"
        }
    },
    BreakdownGroupRows: {
        seriesLabel: "Series {{letter}}: {{label}}",
        remove: "Remove {{label}}"
    },
    ComparisonSeriesRow: {
        seriesLabel: "Series {{letter}}: {{label}}"
    },
    ComparisonFilterChips: {
        addFilter: "Add filter",
        remove: "Remove filter",
        anyValue: "Any",
        valueCount_one: "{{count}} selected",
        valueCount_other: "{{count}} selected",
        customValue: "Custom",
        directManagers: {
            ariaLabel: "Direct managers: {{value}}"
        },
        reportingLine: {
            ariaLabel: "Reporting lines: {{value}}"
        },
        segment: {
            ariaLabel: "Segment: {{value}}"
        },
        department: {
            ariaLabel: "Department: {{value}}"
        },
        gender: {
            ariaLabel: "Gender: {{value}}"
        },
        workLocation: {
            ariaLabel: "Work location: {{value}}"
        },
        roleLevel: {
            ariaLabel: "Role level: {{value}}"
        },
        jobTitle: {
            ariaLabel: "Job title: {{value}}"
        },
        tenure: {
            ariaLabel: "Tenure: {{value}}"
        },
        track: {
            ariaLabel: "Track: {{value}}"
        }
    },
    ComparisonSeriesLabels: {
        base: "Current view",
        fallback: "Comparison {{number}}",
        partSeparator: " · ",
        managerCount_one: "{{count}} manager",
        managerCount_other: "{{count}} managers"
    },
    PeriodLabels: {
        cycles_one: "{{count}} cycle",
        cycles_other: "{{count}} cycles",
        range: "{{from}} – {{to}}",
        rangeFrom: "From {{from}}",
        rangeTo: "Until {{to}}",
        preset: {
            LastReview: "Last review",
            PastNinetyDays: "Past 90 days",
            LastYear: "Last year",
            LastTwoYears: "Last 2 years"
        }
    },
    ComparisonLegend: {
        ariaLabel: "Comparison series",
        noData: "No data"
    },
    EngagementHeatmapPage: {
        liveRegion: {
            loading: "Loading heatmap"
        },
        cellMode: {
            ariaLabel: "Cell display",
            score: "Score",
            variation: "Variation"
        },
        scoreDateLabel: "Score date",
        sectionError: "The heatmap couldn't be loaded",
        sectionErrorReassurance: "Please try again later.",
        retry: "Try again",
        retrying: "Retrying…"
    },
    EngagementHeatmapTable: {
        ariaLabel: "Engagement heatmap",
        heading: {
            title: "Heatmap",
            description: "Participation, eNPS, and engagement by metric across each manager's team. Sort any column, or expand a metric to see its sub-metrics.",
            teamsCount_one: "{{count}} team",
            teamsCount_other: "{{count}} teams"
        },
        columns: {
            manager: "Manager",
            participation: "Participation",
            enps: "eNPS",
            engagement: "Overall engagement"
        },
        organizationRowLabel: "Organization",
        benchmarkRowLabel: "Benchmark",
        benchmarkTooltip: "External benchmark. Choose the industry, location, and company size to compare against.",
        configureBenchmark: "Configure benchmark industry, location, and company size",
        notEnoughResponses: "Not enough responses",
        noScore: "No score",
        emptyState: "No teams match the current scope and time frame.",
        expandSubMetrics: "{{label}}, expand sub-metrics",
        collapseSubMetrics: "{{label}}, collapse sub-metrics",
        teamRowLabel: "Manager's team",
        sortBy: "Sort by {{label}}",
        sortAscending: "{{label}}, sorted ascending. Activate to sort descending.",
        sortDescending: "{{label}}, sorted descending. Activate to sort ascending.",
        showAllRows: "Show all rows ({{count}})",
        hideLowResponseTeams: "Hide low-response teams",
        increased: "Increased",
        decreased: "Decreased",
        heatTier: {
            extremeGood: "well above peers",
            good: "above peers",
            bad: "below peers",
            extremeBad: "well below peers"
        },
        variationTier: {
            moderate: "moderate change",
            high: "large change"
        },
        pagination: {
            summary: "Showing {{start}}–{{end}} of {{total}}",
            previous: "Previous",
            next: "Next",
            page: "Page {{page}} of {{totalPages}}"
        },
        tooltip: {
            score: "Score {{value}}",
            asOfDate: "As of {{date}}"
        },
        liveRegion: {
            sortChanged: "Sorted by {{column}} {{direction}}",
            ascending: "ascending",
            descending: "descending"
        }
    },
    AtAGlanceSection: {
        heading: "At a glance",
        totalFeedbackLabel: "Total feedback",
        replyRateLabel: "Reply rate",
        unrepliedLabel: "Unreplied messages",
        valueUnavailable: "Not available",
        deltaMessages_one: "{{count}} message",
        deltaMessages_other: "{{count}} messages",
        deltaPoints_one: "{{count}} pt",
        deltaPoints_other: "{{count}} pts",
        deltaUpMessages_one: "up {{count}} message",
        deltaUpMessages_other: "up {{count}} messages",
        deltaDownMessages_one: "down {{count}} message",
        deltaDownMessages_other: "down {{count}} messages",
        deltaUpPoints_one: "up {{count}} point",
        deltaUpPoints_other: "up {{count}} points",
        deltaDownPoints_one: "down {{count}} point",
        deltaDownPoints_other: "down {{count}} points",
        deltaSuffix: {
            "30d": "vs prior month",
            "3m": "vs prior 3 months",
            "6m": "vs prior 6 months",
            quarter: "vs prior quarter",
            year: "vs prior year",
            custom: "vs prior period"
        },
        deltaFlatSuffix: {
            "30d": "No change from prior month",
            "3m": "No change from prior 3 months",
            "6m": "No change from prior 6 months",
            quarter: "No change from prior quarter",
            year: "No change from prior year",
            custom: "No change from prior period"
        },
        emptyHeading: "Not enough feedback yet",
        emptyBody: "Your feedback total, reply rate, and unreplied count will appear here once there's enough feedback.",
        errorHeading: "Couldn't load feedback overview",
        errorBody: "Something went wrong while loading your feedback totals and reply rate.",
        errorRetry: "Retry",
        errorRetrying: "Retrying…",
        liveRegion: {
            loading: "Loading feedback overview"
        }
    },
    SentimentSection: {
        heading: "Sentiment",
        showTrend: "Show trend",
        hideTrend: "Hide trend",
        positiveLabel: "Positive",
        constructiveLabel: "Constructive",
        deltaPoints_one: "{{count}} pt",
        deltaPoints_other: "{{count}} pts",
        deltaUp_one: "up {{count}} point",
        deltaUp_other: "up {{count}} points",
        deltaDown_one: "down {{count}} point",
        deltaDown_other: "down {{count}} points",
        deltaSuffix: {
            "30d": "vs prior month",
            "3m": "vs prior 3 months",
            "6m": "vs prior 6 months",
            quarter: "vs prior quarter",
            year: "vs prior year",
            custom: "vs prior period"
        },
        deltaFlatSuffix: {
            "30d": "No change from prior month",
            "3m": "No change from prior 3 months",
            "6m": "No change from prior 6 months",
            quarter: "No change from prior quarter",
            year: "No change from prior year",
            custom: "No change from prior period"
        },
        chartTitle: "Sentiment over time",
        chartAlt: "Share of positive, constructive, and no-sentiment feedback over the selected period",
        chartYAxisLabel: "Messages",
        legendPositive: "Positive",
        legendConstructive: "Constructive",
        legendNoSentiment: "No sentiment",
        emptyHeading: "Not enough feedback yet",
        emptyBody: "Positive and constructive sentiment, and how they trend, will appear here once there's enough feedback.",
        historyErrorBody: "Trend data couldn't load",
        errorHeading: "Couldn't load sentiment data",
        errorBody: "Something went wrong while loading the sentiment overview.",
        errorRetry: "Retry",
        errorRetrying: "Retrying…",
        liveRegion: {
            loading: "Loading sentiment overview"
        }
    },
    BenchmarkOptionsDialog: {
        title: "Setup benchmark",
        description: "Choose the industry, location, and company size that best match your organization. This helps you compare your results to similar organizations.",
        industryLabel: "Industry",
        locationLabel: "Location",
        companySizeLabel: "Organization size",
        cancel: "Cancel",
        confirm: "Confirm",
        industries: {
            All: "All industries",
            NaturalResources: "Natural resources",
            UtilitiesAndServices: "Utilities and services",
            Construction: "Construction",
            Manufacturing: "Manufacturing",
            WholesaleAndRetailTrade: "Wholesale and retail trade",
            TransportationAndWarehousing: "Transportation and warehousing",
            InformationAndCulturalIndustries: "Information and cultural industries",
            FinancialServicesOrInsurance: "Financial services, insurance",
            RealEstateAndRentalOrLeasing: "Real estate, rental and leasing",
            ProfessionalScientificTechnicalServices: "Professional, scientific and technical services",
            ManagementConsulting: "Management consulting",
            EducationalServices: "Educational services",
            HealthcareAndSocialAssistance: "Healthcare and social assistance",
            ArtsEntertainmentAndRecreation: "Arts, entertainment and recreation",
            AccommodationAndFoodServices: "Hospitality and food services",
            PublicAndGovernmentAdministration: "Public and government administration",
            ComputerSoftwareItServices: "Computer software, IT and services",
            NonprofitOrganization: "Non-profit",
            MarketingAndAdvertising: "Marketing and advertising"
        },
        locations: {
            All: "All locations",
            APAC: "Asia",
            AustraliaAndNewZealand: "Australia and New Zealand",
            Canada: "Canada",
            Europe: "Europe",
            LatinAmerica: "Latin America",
            MiddleEastAndAfrica: "Middle East and Africa",
            NorthAmerica: "North America",
            UnitedStates: "United States"
        },
        companySizes: {
            AllRanges: "All organization sizes",
            OneTo99: "1-99 employees",
            OneHundredTo249: "100-249 employees",
            TwoHundredFiftyTo999: "250-999 employees",
            OneThousandAndMore: "More than 1000 employees"
        }
    },
    TopicSignalsCard: {
        title: "Topic signals",
        newPill: "New",
        description: "Highlight topics showing significant changes or unusual patterns. Topics come from a curated list of 250, built by our data scientists and HR experts. When a topic meets the minimum threshold, it gets one of three labels:",
        legend: {
            Risk: "an existing or emerging issue trending the wrong way.",
            Trending: "notable positive momentum or an unusual shift.",
            New: "a topic that surfaced for the first time this period."
        },
        signals: {
            Risk: "Risk",
            Trending: "Trending",
            New: "New"
        },
        rowDescription: {
            Risk: "A rise in constructive feedback on this topic. Review the feedback to see what’s behind it.",
            Trending: "A clear jump in feedback volume, with sentiment holding steady.",
            New: "This topic just appeared. It was not present in feedback from the previous period. Take a look at what’s emerging."
        },
        columns: {
            signal: "Signal",
            topic: "Topic",
            volume: "Volume"
        },
        listLabel: "Topic signals",
        volume_one: "{{formattedCount}} message",
        volume_other: "{{formattedCount}} messages",
        empty: {
            title: "Not enough feedback yet",
            description: "Topic signals and trends will appear here once there's enough feedback to work with."
        },
        sentimentFiltered: {
            title: "No topic signals to show here",
            description: "Topic signals aren't available when filtering by sentiment. Clear the filter to see them."
        },
        error: "We couldn't load topic signals right now.",
        errorReassurance: "Please try again later.",
        retry: "Try again",
        retrying: "Retrying…",
        liveRegion: {
            loading: "Loading topic signals"
        }
    },
    GoodVibesDeltaChip: {
        variation: "{{percent}}% {{comparison}}",
        increased: "Increase",
        decreased: "Decrease",
        noComparison: "No comparison available",
        comparison: {
            "7d": "vs prior 7 days",
            "30d": "vs prior 30 days",
            "3m": "vs prior 3 months",
            "6m": "vs prior 6 months",
            quarter: "vs last quarter",
            year: "vs last year"
        }
    },
    GoodVibesTotalReceivedCard: {
        title: "Total Good Vibes received",
        valueLabel: "Good Vibes received",
        period: {
            "7d": "Last 7 days",
            "30d": "Last 30 days",
            "3m": "Last 3 months",
            "6m": "Last 6 months",
            quarter: "This quarter",
            year: "This year"
        },
        error: "We couldn't load Good Vibes received right now.",
        errorReassurance: "Please try again later.",
        retry: "Try again",
        retrying: "Retrying…",
        liveRegion: {
            loading: "Loading Good Vibes received"
        }
    }
};
